import { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { toast } from 'sonner';
import {
  Send, Sparkles, Plus, Trash2, Edit2, ChevronDown, Search,
  Paperclip, Loader2, MessageSquare, Crown, Lock, FileText, ImageIcon, X,
  Brain, Menu, Save, Check, ExternalLink,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../components/ui/tooltip';
import {
  AnthropicMark, OpenAIMark, GeminiMark, MetaMark, PerplexityMark,
} from '../components/landing/BrandLogos';
import { CBMark } from '../components/CBMark';
import { CBLogo } from '../components/CBLogo';
import { cn } from '../lib/utils';
import { SaveChatButton } from '../components/ai-hub/SaveChatButton';
import { PromptLibrary } from '../components/ai-hub/PromptLibrary';
import IntentActionCard from '../components/ai-hub/IntentActionCard';
import MemoryPanel from '../components/ai-hub/MemoryPanel';
import CommandHero from '../components/CommandHero';
import ContinueLastConversation from '../components/ContinueLastConversation';
import SetupChecklistInline from '../components/SetupChecklistInline';
import MarkdownView from '../components/MarkdownView';
import SpeakButton from '../components/SpeakButton';
import { useVoiceAutoSend } from '../hooks/useVoiceAutoSend';
// Extracted presentational components — see /components/ai-hub/
import { providerMarkFor, TOP_PROVIDERS } from '../components/ai-hub/providerMarks';
import { EmptyState } from '../components/ai-hub/EmptyState';
import { MessageBubble } from '../components/ai-hub/MessageBubble';
import { ThreadSidebar } from '../components/ai-hub/ThreadSidebar';
import { ModelPicker } from '../components/ai-hub/ModelPicker';
import { Composer } from '../components/ai-hub/Composer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIHub({ heroMode = false } = {}) {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();

  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [models, setModels] = useState([]);
  const [tier, setTier] = useState('paid');
  const [chosenModel, setChosenModel] = useState(null);
  const [modelSearch, setModelSearch] = useState('');
  const [pendingAttachment, setPendingAttachment] = useState(null);
  // Intent-router action card: rendered inline in the chat when the user's
  // prompt looks like an actionable command (e.g. "log $42 lunch at Joe's").
  // Confirming runs the action; cancelling falls back to a normal AI chat.
  const [pendingIntent, setPendingIntent] = useState(null);
  const [classifyingIntent, setClassifyingIntent] = useState(false);
  // Mobile: conversations sidebar overlay state (auto-collapsed by default).
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Brain mode — when ON the chat routes through /brain-message which gives
  // the AI structured tool access to the workspace (financials, docs, etc.).
  // Default ON per user spec ("every chat is workspace aware"). User can
  // toggle off for generic non-workspace questions.
  const [brainMode, setBrainMode] = useState(() => {
    try { return localStorage.getItem('ai_studio_brain_off') !== '1'; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('ai_studio_brain_off', brainMode ? '0' : '1'); }
    catch { /* */ }
  }, [brainMode]);

  // Image-mode toggle — when ON, forces Nano Banana to run regardless of
  // the intent-detector regex. Lets users refine an existing image with
  // short prompts ("darker", "add a coffee cup") and gives a clear visible
  // affordance so the AI doesn't refuse with "I can't generate images".
  const [imageMode, setImageMode] = useState(false);
  // Cross-thread memory sheet — opens from the composer toolbar.
  const [memoryOpen, setMemoryOpen] = useState(false);

  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const sendMessageRef = useRef(null);
  // Voice→Quick Buck auto-send state — extracted into a custom hook so the
  // race-condition guards (pendingAutoSend, autoSendActiveRef, messagesRef,
  // setMessagesSync) live in one place. See hooks/useVoiceAutoSend.js for the
  // 3-layer defense story.
  const {
    pendingAutoSend: pendingAutoSendRef,
    isAutoSendActive,
    releaseAutoSend,
    messagesRef,
    setMessagesSync,
  } = useVoiceAutoSend({ messages, setMessages });
  const [searchParams, setSearchParams] = useSearchParams();

  // Load threads + models on mount (apiFetch already attaches auth headers
  // via the global fetchInterceptor — no need for a per-call headers prop).
  useEffect(() => {
    if (!user) return;
    loadThreads();
    loadModels();
  }, [user]); // eslint-disable-line

  // Handle ?thread=... and ?promptHub=... query params from AI Command Center
  // links. After consuming, strip the params so the URL doesn't keep stale state.
  useEffect(() => {
    const threadParam = searchParams.get('thread');
    if (threadParam && threadParam !== activeId) {
      setActiveId(threadParam);
      searchParams.delete('thread');
      setSearchParams(searchParams, { replace: true });
    }
    // promptHub is consumed by PromptLibrary (read on open) — leave it in URL
    // for now so the user sees the filter applied; clear after a short delay.

    // ?prefill=1 — Command Hero handoff. Read the prompt from sessionStorage
    // (we avoid the URL to support long prompts) and load it into the composer
    // WITHOUT auto-sending so the user can tweak it first. Then strip the
    // param + sessionStorage key so reloads don't re-trigger.
    if (searchParams.get('prefill') === '1') {
      try {
        // Prefer the value we captured synchronously on init; fall back to
        // sessionStorage if a later navigation injects a fresh prefill.
        const prefill = pendingAutoSendRef.current
          || sessionStorage.getItem('ai_studio_prefill_prompt');
        if (prefill) {
          setInput(prefill);
          sessionStorage.removeItem('ai_studio_prefill_prompt');
          // pendingAutoSendRef.current was set on init when autoSend=1; no-op
          // here unless this is a mid-session prefill (then we don't auto-send).
        }
      } catch { /* ignore storage errors */ }
      // Strip params so reloads don't re-trigger.
      searchParams.delete('autoSend');
      searchParams.delete('prefill');
      setSearchParams(searchParams, { replace: true });
    }

    // Command Center → AI Hub handoff. The Dashboard's prompt chips +
    // composer navigate here with `?seed=<prompt>` so the user lands inside
    // AI Hub with the question pre-typed (but NOT auto-sent — gives them a
    // chance to tweak before firing).
    const seed = searchParams.get('seed');
    if (seed) {
      setInput(seed);
      searchParams.delete('seed');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams]); // eslint-disable-line

  // Load messages when active thread changes.
  // Important: only setMessages([]) when transitioning AWAY from a thread
  // (activeId WAS set, now isn't). Don't clobber optimistic UI on initial
  // mount when activeId is still settling — that's what wiped the auto-send
  // action card before this guard was added.
  const prevActiveIdRef = useRef(null);
  useEffect(() => {
    // During a Voice→Quick Buck auto-send, suppress loadMessages so it can't
    // wipe the optimistic [user, card] insert if loadThreads' auto-select
    // sneaks in. The flag is cleared inside sendMessage once the optimistic
    // commit is in state.
    if (isAutoSendActive()) {
      prevActiveIdRef.current = activeId;
      return;
    }
    if (activeId) {
      loadMessages(activeId);
    } else if (prevActiveIdRef.current) {
      // We *had* a thread, now we don't — safe to clear.
      setMessages([]);
    }
    prevActiveIdRef.current = activeId;
  }, [activeId]); // eslint-disable-line

  // Auto-send watcher — fires sendMessage exactly once after the prefill from
  // a voice handoff lands AND the thread state has settled. Gated by a ref
  // so we never double-fire on re-renders.
  useEffect(() => {
    if (!pendingAutoSendRef.current) return;
    if (input !== pendingAutoSendRef.current) return;  // wait for setInput to commit
    if (sending || classifyingIntent) return;
    pendingAutoSendRef.current = null;
    // Small tick so any concurrent state updates flush first.
    const t = setTimeout(() => { sendMessageRef.current?.(); }, 60);
    return () => clearTimeout(t);
  }, [input, sending, classifyingIntent]); // eslint-disable-line

  // Autoscroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadThreads = async () => {
    try {
      const r = await apiFetch(`${API_URL}/api/ai-hub/threads`);
      const data = await r.json();
      setThreads(data.threads || []);
      // In heroMode we DON'T auto-open a thread — the user lands on the empty
      // Command Hero state, and a thread is only created when they hit Send.
      // Also skip auto-select when a Voice→Quick Buck auto-send is pending,
      // otherwise loadMessages(autoSelectedId)→setMessages([]) wipes the
      // optimistic action-card insert we're about to make. Finally, never
      // auto-select if the user already has visible messages — that means
      // sendMessage has already committed an optimistic insert and a late
      // loadThreads resolution must not steal focus.
      if (
        !heroMode
        && !activeId
        && data.threads?.length
        && !pendingAutoSendRef.current
        && !isAutoSendActive()
        && messagesRef.current.length === 0
      ) {
        setActiveId(data.threads[0].thread_id);
      }
      // The mount loadThreads has settled and made its auto-select decision —
      // no future loadThreads call can race the optimistic insert, so it's
      // safe to release the auto-send guard. User-driven thread clicks after
      // this point will go through loadMessages normally.
      releaseAutoSend();
    } catch (e) {
      console.error('Load threads failed', e);
      // Defensive: if loadThreads errored, sendMessage's optimistic-insert
      // clear is the only one that runs. But if sendMessage also fails before
      // committing (e.g. classify network error), the ref would stick around
      // and block future thread navigation. Release it here too.
      releaseAutoSend();
    }
  };

  const loadModels = async () => {
    try {
      const r = await apiFetch(`${API_URL}/api/ai-hub/models`);
      const data = await r.json();
      setModels(data.models || []);
      setTier(data.tier || 'paid');
      setChosenModel(data.default);
    } catch (e) {
      console.error('Load models failed', e);
    }
  };

  // Lightweight billing snapshot — used to surface a "save credits, switch
  // to free Llama 3.3" hint near the cap. Refetches every 60s.
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    if (!currentWorkspace?.workspace_id) return;
    let cancelled = false;
    const fetchUsage = async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/billing/ai-usage?workspace_id=${currentWorkspace.workspace_id}`);
        if (r.ok && !cancelled) setUsage(await r.json());
      } catch { /* silent */ }
    };
    fetchUsage();
    const t = setInterval(fetchUsage, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [currentWorkspace?.workspace_id]);

  const freeFallbackModel = useMemo(() => {
    if (!models.length) return null;
    const llama = models.find((m) => m.is_free && m.id.includes('llama'));
    return llama || models.find((m) => m.is_free) || null;
  }, [models]);

  const showFreeFallbackHint = !!(
    usage
    && usage.plan_id !== 'free'
    && usage.ai_status !== 'capped'
    && (usage.ai_pct_used || 0) >= 70
    && freeFallbackModel
    && chosenModel
    && !chosenModel.endsWith(':free')
  );

  const loadMessages = async (threadId) => {
    setLoadingMessages(true);
    try {
      const r = await apiFetch(`${API_URL}/api/ai-hub/threads/${threadId}/messages`);
      const data = await r.json();
      setMessages(data.messages || []);
      if (data.thread?.model) setChosenModel(data.thread.model);
    } catch (e) {
      console.error('Load messages failed', e);
    } finally {
      setLoadingMessages(false);
    }
  };

  // ─── Background-reply polling ──────────────────────────────────────
  // When a message is sent in background mode the backend returns a
  // "pending" assistant placeholder. The LLM finishes async; the
  // frontend has no idea WHEN — so we poll the thread every 2s while
  // any pending bubble is visible. The poll calls the lightweight
  // /pending endpoint (sub-50ms) and only re-fetches the full message
  // list when pending flips to false. Stops automatically when no
  // pending bubble is in state.
  const anyPendingInActive = messages.some(
    (m) => m.role === 'assistant' && m.status === 'pending',
  );
  useEffect(() => {
    if (!activeId || !anyPendingInActive) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await apiFetch(
          `${API_URL}/api/ai-hub/threads/${activeId}/pending`,
        );
        if (!r.ok || cancelled) return;
        const data = await r.json();
        if (!data.any_pending) {
          // Pending bubble has resolved (completed or failed) — refetch
          // the full message list so the placeholder is replaced with
          // the finished reply (incl. any PDF / spreadsheet / image
          // attachments the backend stamped onto it).
          await loadMessages(activeId);
          // Also refresh the sidebar so the (possibly newly-renamed)
          // title shows up after the LLM finishes thinking.
          loadThreads();
        }
      } catch (_e) {
        // Network blips are fine — we'll try again on the next tick.
      }
    };
    const id = setInterval(tick, 2000);
    // Fire one immediate poll so a fast reply isn't stuck waiting 2s.
    tick();
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, anyPendingInActive]);

  // ─── Cross-thread pending badge ────────────────────────────────────
  // The user can navigate away from the AI Hub while a background reply
  // is generating. We expose `globalPendingCount` so the sidebar nav
  // item (and the in-Hub thread list) can render a small badge: "1
  // reply incoming…". Poll every 5s as long as the page is open.
  const [globalPendingCount, setGlobalPendingCount] = useState(0);
  const [globalPendingThreadIds, setGlobalPendingThreadIds] = useState([]);
  useEffect(() => {
    if (!currentWorkspace?.workspace_id) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await apiFetch(
          `${API_URL}/api/ai-hub/pending-replies?workspace_id=${currentWorkspace.workspace_id}`,
        );
        if (!r.ok || cancelled) return;
        const data = await r.json();
        setGlobalPendingCount(data.count || 0);
        setGlobalPendingThreadIds(
          (data.pending || []).map((p) => p.thread_id),
        );
        // If a thread that was pending is no longer pending AND it's
        // not the active one, refresh the threads list so the user
        // sees the new title / preview when they switch to it.
        if ((data.count || 0) === 0 && globalPendingCount > 0) {
          loadThreads();
        }
      } catch (_e) { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?.workspace_id]);

  const createThread = async () => {
    try {
      const r = await apiFetch(`${API_URL}/api/ai-hub/threads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: currentWorkspace?.workspace_id,
          model: chosenModel,
        }),
      });
      const data = await r.json();
      setThreads([data, ...threads]);
      setActiveId(data.thread_id);
      setMessages([]);
    } catch (e) {
      toast.error('Could not create chat');
    }
  };

  const [backfillingTitles, setBackfillingTitles] = useState(false);
  // Per-thread retitle spinner — only one at a time. Set to the thread_id
  // currently being retitled so the sidebar can disable its button.
  const [retitlingId, setRetitlingId] = useState(null);
  const retitleThread = async (threadId) => {
    if (retitlingId) return;
    setRetitlingId(threadId);
    try {
      const r = await apiFetch(`${API_URL}/api/ai-hub/threads/${threadId}/retitle`, {
        method: 'POST',
      });
      if (r.ok) {
        const data = await r.json();
        if (data.reason === 'no_change' || data.reason === 'no_messages' || data.reason === 'empty_messages') {
          toast.info(data.reason === 'no_change' ? 'Title already fits the conversation' : 'Not enough conversation yet to retitle');
        } else {
          toast.success(`Renamed to "${data.title}"`);
          // Optimistic local update + full refresh so any other clients see it too.
          setThreads((prev) => prev.map((t) =>
            t.thread_id === threadId ? { ...t, title: data.title, auto_titled: true } : t
          ));
          loadThreads();
        }
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || `Retitle failed (HTTP ${r.status})`);
      }
    } catch (e) {
      toast.error('Retitle failed');
    } finally {
      setRetitlingId(null);
    }
  };

  const backfillTitles = async () => {
    if (backfillingTitles) return;
    setBackfillingTitles(true);
    try {
      const r = await apiFetch(`${API_URL}/api/ai-hub/threads-backfill-titles`, {
        method: 'POST',
      });
      if (r.ok) {
        const data = await r.json();
        if (data.retitled > 0) {
          toast.success(`Auto-titled ${data.retitled} chat${data.retitled === 1 ? '' : 's'}`);
        } else {
          toast.info('Nothing to retitle');
        }
        loadThreads();
        // The LLM refinement lands a second later — fetch again then.
        setTimeout(() => loadThreads(), 6000);
      } else {
        toast.error('Backfill failed');
      }
    } catch (e) {
      toast.error('Backfill failed');
    } finally {
      setBackfillingTitles(false);
    }
  };

  const deleteThread = async (threadId) => {
    if (!window.confirm('Delete this conversation? This cannot be undone.')) return;
    try {
      const r = await apiFetch(`${API_URL}/api/ai-hub/threads/${threadId}`, { method: 'DELETE' });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        // Bubble the actual server reason — we used to swallow it into a
        // useless "Delete failed" which made the prod-only bug ticket
        // unsolvable. (Jun 16, 2026.)
        toast.error(d.detail || `Delete failed (HTTP ${r.status})`);
        return;
      }
      const filtered = threads.filter((t) => t.thread_id !== threadId);
      setThreads(filtered);
      if (activeId === threadId) {
        setActiveId(filtered[0]?.thread_id || null);
      }
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
  };

  const saveRename = async () => {
    if (!renamingId || !renameValue.trim()) {
      setRenamingId(null);
      return;
    }
    try {
      await apiFetch(`${API_URL}/api/ai-hub/threads/${renamingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: renameValue.trim() }),
      });
      setThreads(threads.map((t) =>
        t.thread_id === renamingId ? { ...t, title: renameValue.trim() } : t));
    } catch (e) {
      toast.error('Rename failed');
    } finally {
      setRenamingId(null);
      setRenameValue('');
    }
  };

  const uploadFile = async (file) => {
    if (!file || !activeId) {
      if (!activeId) toast.error('Open or create a chat first');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10 MB)');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await apiFetch(`${API_URL}/api/ai-hub/threads/${activeId}/attach`, {
        method: 'POST',
        body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Upload failed');
        return;
      }
      const data = await r.json();
      setPendingAttachment({ ...data, original_name: file.name });
      toast.success(`Attached: ${file.name}`);
    } catch {
      toast.error('Upload failed');
    }
  };

  const handleAttach = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    await uploadFile(file);
  };

  const sendMessage = async (overrideText) => {
    const isOverride = typeof overrideText === 'string';
    let threadId = activeId;
    const text = (isOverride ? overrideText : input).trim();
    if (!text || sending || classifyingIntent) return;

    // ── Intent router: peek at the prompt before sending to the LLM. If it
    // looks like an actionable command (create bill, log time, send reminder,
    // mark paid, lookup contact), surface an editable action card inline and
    // let the user confirm before mutating anything. Otherwise fall through
    // to the regular AI chat flow.
    if (!pendingAttachment && !isOverride) {
      setClassifyingIntent(true);
      try {
        const r = await apiFetch(`${API_URL}/api/ai-hub/intent/classify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt: text, workspace_id: currentWorkspace?.workspace_id }),
        });
        if (r.ok) {
          const plan = await r.json();
          // Multi-step chain: render multiple stacked IntentActionCards in
          // a single assistant bubble. Each card is independently editable
          // and confirmable so the user can review write-actions one by one.
          if (plan.is_chain && Array.isArray(plan.intents) && plan.intents.length >= 2) {
            const userMsgId = `tmp_user_${Date.now()}`;
            const cardMsgId = `tmp_chain_${Date.now()}`;
            setMessagesSync((prev) => [
              ...prev,
              {
                message_id: userMsgId,
                role: 'user',
                content: text,
                attachments: [],
                created_at: new Date().toISOString(),
              },
              {
                message_id: cardMsgId,
                role: 'assistant',
                content: '',
                action_plans: plan.intents,
                chain_summary: plan.summary,
                created_at: new Date().toISOString(),
              },
            ]);
            setInput('');
            setClassifyingIntent(false);
            // Track the chain by its bubble id; we don't pre-pin a single
            // pendingIntent because the user confirms each card individually.
            setPendingIntent({ planId: cardMsgId, plan, isChain: true });
            releaseAutoSend();
            return;
          }
          if (plan.intent !== 'chat' && (plan.confidence || 0) >= 0.6) {
            // Inject the user's message + action card into the visible thread.
            const userMsgId = `tmp_user_${Date.now()}`;
            const cardMsgId = `tmp_card_${Date.now()}`;
            setMessagesSync((prev) => [
              ...prev,
              {
                message_id: userMsgId,
                role: 'user',
                content: text,
                attachments: [],
                created_at: new Date().toISOString(),
              },
              {
                message_id: cardMsgId,
                role: 'assistant',
                content: '',
                action_plan: plan,
                created_at: new Date().toISOString(),
              },
            ]);
            setInput('');
            setClassifyingIntent(false);
            setPendingIntent({ planId: cardMsgId, plan });
            // Optimistic insert is now in state — safe to release the
            // auto-send guard so subsequent thread navigation behaves normally.
            releaseAutoSend();
            return;
          }
        }
      } catch {
        // Classify is non-essential — silently fall through to normal chat.
      } finally {
        setClassifyingIntent(false);
      }
    }

    // If no thread is open, create one on-the-fly so the user can just start typing
    if (!threadId) {
      const r = await apiFetch(`${API_URL}/api/ai-hub/threads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: currentWorkspace?.workspace_id,
          model: chosenModel,
        }),
      });
      const t = await r.json();
      threadId = t.thread_id;
      setThreads([t, ...threads]);
      setActiveId(threadId);
    }

    const att = pendingAttachment;
    // Compose final content — if attachment is a PDF, inline the extracted text
    let finalContent = text;
    if (att?.kind === 'pdf_text' && att.text) {
      finalContent = `${text}\n\n--- Attached PDF: ${att.original_name} ---\n${att.text}`;
    }
    const attachmentsForUser = att
      ? [{ kind: att.kind, filename: att.original_name, url: att.url || null, staged_file_id: att.staged_file_id || null }]
      : [];

    // Optimistic UI for the user's message
    const optimistic = {
      message_id: `tmp_${Date.now()}`,
      role: 'user',
      content: text,
      attachments: attachmentsForUser,
      created_at: new Date().toISOString(),
    };
    setMessagesSync((prev) => [...prev, optimistic]);
    setInput('');
    setPendingAttachment(null);
    setSending(true);
    // Optimistic insert committed — release the auto-send guard so future
    // thread switches behave normally (intent path returns earlier; this
    // covers the non-intent / pure-chat auto-send path).
    releaseAutoSend();

    try {
      // Route to the Brain endpoint when workspace-aware mode is on (default).
      // The Brain endpoint runs a tool-calling loop with the model so it can
      // look up live workspace data (cash, invoices, docs, etc.) AND perform
      // tasks (categorize transactions, file documents) — attachments are now
      // fully supported in brain mode (images go multimodal, files are staged
      // so the AI can save them to Documents on request).
      const useBrain = brainMode;
      const endpoint = useBrain
        ? `${API_URL}/api/ai-hub/threads/${threadId}/brain-message`
        : `${API_URL}/api/ai-hub/threads/${threadId}/messages`;
      const r = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: finalContent,
          model: chosenModel,
          workspace_id: currentWorkspace?.workspace_id,
          attachments: attachmentsForUser,
          image_mode: imageMode,
          // Background mode: backend returns a pending placeholder
          // immediately and finishes the LLM work async. The user can
          // navigate away (or close the tab) while the reply generates.
          // brain-mode runs synchronously today (tool-calling loop), so
          // only flip the bit for the standard endpoint.
          background: !useBrain,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        if (r.status === 402) {
          toast.error(err.detail || 'Daily free tier limit reached');
        } else {
          toast.error(err.detail || 'Message failed');
        }
        setMessages((prev) => prev.filter((m) => m.message_id !== optimistic.message_id));
        // Title gets set BEFORE the chat completion happens, so refresh the
        // sidebar even on error paths so the user sees their newly named
        // chat (e.g., when OpenRouter free models are rate-limited).
        loadThreads();
        setTimeout(() => loadThreads(), 6000);
        return;
      }
      const data = await r.json();
      setMessages((prev) =>
        prev
          .filter((m) => m.message_id !== optimistic.message_id)
          .concat([data.user_message, data.assistant_message]),
      );
      // Refresh threads now to pick up the instant heuristic auto-title,
      // then again ~6s later to grab the LLM-refined title that lands
      // asynchronously after the response returns.
      loadThreads();
      setTimeout(() => loadThreads(), 6000);
    } catch (e) {
      console.error(e);
      toast.error('Network error');
      setMessages((prev) => prev.filter((m) => m.message_id !== optimistic.message_id));
      // Same reasoning as above — title may have been set before the error.
      loadThreads();
      setTimeout(() => loadThreads(), 6000);
    } finally {
      setSending(false);
    }
  };

  /**
   * Save a Nano Banana image to the workspace's Documents Hub. Stores the
   * image URL + prompt + filename so the user can search/find it later.
   * Returns the saved document descriptor on success (or null on failure)
   * so the SaveImageButton can render a deep-link to view it.
   */
  const saveImageToDocuments = async ({ imageUrl, imageFilename, imagePrompt }) => {
    let threadId = activeId;
    if (!threadId || !imageUrl) return null;
    if (!currentWorkspace?.workspace_id) {
      toast.error('Pick a workspace first to save images.');
      return null;
    }
    try {
      const r = await apiFetch(`${API_URL}/api/ai-hub/threads/${threadId}/save-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_url: imageUrl,
          filename: imageFilename,
          prompt: imagePrompt,
          workspace_id: currentWorkspace.workspace_id,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save image');
        return null;
      }
      const data = await r.json();
      toast.success(data.message || 'Saved to Documents → AI Images');
      return {
        documentId: data.document?.document_id,
        folderId: data.folder_id || data.document?.folder_id,
      };
    } catch (e) {
      console.error(e);
      toast.error('Network error — could not save image');
      return null;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  /**
   * Refine an existing Nano Banana image. Sends a NEW chat turn with the
   * previous image attached as an `image_url` reference + the user's
   * refinement prompt prefixed with "edit this image:" so the backend's
   * `wants_image()` detector fires. Bypasses brain mode (which doesn't
   * accept attachments) and skips the intent classifier (this isn't a
   * write-action — it's an image edit).
   */
  const sendImageRefinement = async ({ imageUrl, imageFilename, refinement }) => {
    let threadId = activeId;
    const trimmed = (refinement || '').trim();
    if (!trimmed || !imageUrl || sending) return;
    if (!threadId) {
      // Create a thread on the fly if needed
      const r = await apiFetch(`${API_URL}/api/ai-hub/threads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!r.ok) {
        toast.error('Could not start a conversation');
        return;
      }
      const data = await r.json();
      threadId = data.thread_id;
      setActiveId(threadId);
    }

    const finalContent = `edit this image: ${trimmed}`;
    const refAttachment = {
      kind: 'image_url',
      url: imageUrl,
      filename: imageFilename || 'reference.png',
    };
    const optimisticUserId = `tmp_user_refine_${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        message_id: optimisticUserId,
        role: 'user',
        content: trimmed,  // show the user's actual words, not the prefix
        attachments: [refAttachment],
        created_at: new Date().toISOString(),
      },
    ]);
    setSending(true);
    try {
      const r = await apiFetch(`${API_URL}/api/ai-hub/threads/${threadId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: finalContent,
          model: chosenModel,
          workspace_id: currentWorkspace?.workspace_id,
          attachments: [refAttachment],
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Refinement failed');
        setMessages((prev) => prev.filter((m) => m.message_id !== optimisticUserId));
        return;
      }
      const data = await r.json();
      setMessages((prev) =>
        prev
          .filter((m) => m.message_id !== optimisticUserId)
          .concat([data.user_message, data.assistant_message]),
      );
      loadThreads();
    } catch (e) {
      console.error(e);
      toast.error('Network error');
      setMessages((prev) => prev.filter((m) => m.message_id !== optimisticUserId));
    } finally {
      setSending(false);
    }
  };

  const visibleModels = useMemo(() => {
    if (!modelSearch) return models;
    const q = modelSearch.toLowerCase();
    return models.filter(
      (m) => m.name.toLowerCase().includes(q) || m.id.toLowerCase().includes(q),
    );
  }, [models, modelSearch]);

  // Group models for the dropdown — Auto pinned at the top, then Big-4
  // providers (GPT, Claude, Gemini, Llama), each in its own section,
  // then "Other models". Models within each provider get sub-sorted by
  // tier (fast → balanced → premium) so $-tier reads top-to-bottom.
  const groupedModels = useMemo(() => {
    const tierRank = { fast: 0, balanced: 1, premium: 2, unknown: 3, free: -1, auto: -2 };
    const autoEntry = visibleModels.find((m) => m.is_auto);
    const groups = TOP_PROVIDERS.map((p) => ({ ...p, models: [] }));
    const other = [];
    visibleModels.forEach((m) => {
      if (m.is_auto) return; // pinned separately
      const id = (m.id || '').toLowerCase();
      const hit = groups.find((g) => id.startsWith(`${g.key}/`));
      if (hit) hit.models.push(m);
      else other.push(m);
    });
    // Sort each provider list by tier
    const byTier = (a, b) => (tierRank[a.tier] ?? 99) - (tierRank[b.tier] ?? 99);
    groups.forEach((g) => g.models.sort(byTier));
    other.sort(byTier);
    return { auto: autoEntry, groups: groups.filter((g) => g.models.length > 0), other };
  }, [visibleModels]);

  const activeModel = models.find((m) => m.id === chosenModel);

  // Vision-capability check for the composer warning. When the user attaches
  // an image but their active model is text-only (e.g. openai/gpt-oss-120b:free),
  // surface a one-click prompt to switch to a vision-capable model. Without
  // this nudge the user spends tokens on a model that literally can't see
  // the image and gets confusing "please upload your logo" replies.
  const imageAttached = pendingAttachment?.kind === 'image_url';
  const modelLacksVision = imageAttached && activeModel && activeModel.supports_image === false;
  const suggestedVisionModel = useMemo(() => {
    if (!modelLacksVision) return null;
    // For free users, suggest a free vision model; for paid, the best one.
    const free = models.find((m) => m.supports_image && m.is_free);
    if (tier === 'free') return free || null;
    // Prefer Gemini 2.5 Pro → GPT-4o → Claude Sonnet 4.5 → any vision model
    const priority = ['google/gemini-2.5-pro', 'openai/gpt-4o', 'anthropic/claude-sonnet-4.5'];
    for (const id of priority) {
      const m = models.find((x) => x.id === id);
      if (m) return m;
    }
    return models.find((m) => m.supports_image) || null;
  }, [modelLacksVision, models, tier]);

  // Keep a ref to the latest sendMessage so effect-triggered auto-sends
  // (Voice → Quick Buck handoff) can dispatch without re-running on every keystroke.
  sendMessageRef.current = sendMessage;

  // ModelPicker — extracted to /components/ai-hub/ModelPicker.jsx
  // We still wrap in a function so callers can pass `size` ergonomically.
  const renderModelPicker = (size = 'sm') => (
    <ModelPicker
      size={size}
      chosenModel={chosenModel}
      setChosenModel={setChosenModel}
      activeModel={activeModel}
      models={models}
      modelSearch={modelSearch}
      setModelSearch={setModelSearch}
      groupedModels={groupedModels}
      tier={tier}
    />
  );

  // Hero state — render only when AIHub is mounted with heroMode AND the user
  // has no active thread yet. This is the unified Command Center landing.
  const showHero = heroMode && !activeId && messages.length === 0;

  if (showHero) {
    return (
      <div
        className="relative min-h-[calc(100vh-3.5rem-4rem)] lg:min-h-[calc(100vh-3.5rem)] bg-zinc-50 dark:bg-black text-zinc-900 dark:text-zinc-100 overflow-y-auto"
        data-testid="ai-hub-hero"
      >
        {/* Gradient mesh background — landing-page DNA */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-60 dark:opacity-100">
          <div className="absolute top-[-15%] left-[-10%] w-[500px] h-[500px] rounded-full bg-emerald-500/15 dark:bg-emerald-500/8 blur-[120px]" />
          <div className="absolute top-[10%] right-[-15%] w-[600px] h-[600px] rounded-full bg-violet-500/15 dark:bg-violet-500/8 blur-[140px]" />
          <div className="absolute bottom-[-25%] left-[30%] w-[700px] h-[700px] rounded-full bg-blue-500/10 dark:bg-blue-500/6 blur-[160px]" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
          {/* Get Started checklist (empty-state only — disappears once a thread opens) */}
          <SetupChecklistInline />
          {/* Big prompt hero. We pass the FULL model dropdown into the slot so
            * users can pick from 200+ models without scrolling into the composer. */}
          <CommandHero
            value={input}
            onChange={setInput}
            onSubmit={() => sendMessage()}
            sending={sending}
            modelPicker={renderModelPicker('sm')}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-[calc(100vh-3.5rem-4rem)] lg:h-[calc(100vh-3.5rem)] bg-zinc-50 dark:bg-black text-zinc-900 dark:text-zinc-100 overflow-hidden" data-testid="ai-hub-page">
      {/* Gradient mesh background — landing-page DNA */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-60 dark:opacity-100">
        <div className="absolute top-[-15%] left-[-10%] w-[500px] h-[500px] rounded-full bg-emerald-500/15 dark:bg-emerald-500/8 blur-[120px]" />
        <div className="absolute top-[10%] right-[-15%] w-[600px] h-[600px] rounded-full bg-violet-500/15 dark:bg-violet-500/8 blur-[140px]" />
        <div className="absolute bottom-[-25%] left-[30%] w-[700px] h-[700px] rounded-full bg-blue-500/10 dark:bg-blue-500/6 blur-[160px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(0,0,0,0.04)_1px,transparent_0)] dark:bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.03)_1px,transparent_0)] [background-size:32px_32px]" />
      </div>

      {/* ─── Sidebar — extracted to /components/ai-hub/ThreadSidebar ──── */}
      <ThreadSidebar
        threads={threads}
        activeId={activeId}
        setActiveId={setActiveId}
        mobileSidebarOpen={mobileSidebarOpen}
        setMobileSidebarOpen={setMobileSidebarOpen}
        createThread={createThread}
        deleteThread={deleteThread}
        renamingId={renamingId}
        setRenamingId={setRenamingId}
        renameValue={renameValue}
        setRenameValue={setRenameValue}
        saveRename={saveRename}
        pendingThreadIds={globalPendingThreadIds}
        onBackfillTitles={backfillTitles}
        backfillingTitles={backfillingTitles}
        onRetitleThread={retitleThread}
        retitlingId={retitlingId}
      />

      {/* ─── Main chat area ─────────────────────────────────────────── */}
      {/* min-w-0 + overflow-x-hidden are CRITICAL on mobile: without min-w-0
        * the flex item refuses to shrink below its content's natural width
        * (composer toolbar buttons + textarea cols default), forcing main to
        * blow past the viewport and triggering horizontal page scroll. */}
      <main className="relative z-10 flex-1 flex flex-col min-w-0 overflow-x-hidden">
        {/* Top bar — active thread title (model picker moved to composer) */}
        <div className="flex-shrink-0 border-b border-zinc-200 dark:border-white/5 px-4 sm:px-6 py-3 flex items-center gap-3 bg-white/60 dark:bg-black/30 backdrop-blur-xl">
          {/* Mobile-only hamburger to open the conversations drawer */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileSidebarOpen(true)}
            className="lg:hidden h-8 w-8 p-0 flex-shrink-0"
            aria-label="Open conversations"
            data-testid="ai-hub-mobile-menu"
          >
            <Menu className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-3 min-w-0">
            <CBLogo size="lg" className="flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500 dark:text-zinc-600 -mb-0.5">
                AI Studio
              </p>
              <h1 className="text-sm font-semibold truncate text-zinc-900 dark:text-zinc-100">
                {threads.find((t) => t.thread_id === activeId)?.title || 'New conversation'}
              </h1>
            </div>
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1">
          <div className="max-w-3xl mx-auto px-3 sm:px-6 py-8 space-y-6">
            {messages.length === 0 && !loadingMessages && !sending && (
              <EmptyState onPrompt={(t) => sendMessage(t)} />
            )}
            {loadingMessages && (
              <div className="text-center text-zinc-500 text-sm py-8">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                Loading conversation…
              </div>
            )}
            {messages
              .filter((m) => !(m.role === 'assistant' && m.status === 'pending'))
              .map((m) => (
              <MessageBubble
                key={m.message_id}
                message={m}
                workspaceId={currentWorkspace?.workspace_id}
                sending={sending}
                isLatest={m.message_id === messages[messages.length - 1]?.message_id}
                onQuickReply={(quickText) => sendMessage(quickText)}
                onRefineImage={sendImageRefinement}
                onSaveImage={saveImageToDocuments}
                onIntentResolved={(result) => {
                  // Replace the action-card message with a friendly confirmation.
                  setMessages((prev) => prev.map((x) =>
                    x.message_id === m.message_id
                      ? { ...x, action_plan: null, action_result: result, content: result.message || 'Done.' }
                      : x
                  ));
                  setPendingIntent(null);
                }}
                onIntentCancel={() => {
                  setMessages((prev) => prev.filter((x) => x.message_id !== m.message_id));
                  setPendingIntent(null);
                }}
              />
            ))}
            {(sending || anyPendingInActive) && (
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center flex-shrink-0">
                  {(() => {
                    const Mark = providerMarkFor(chosenModel);
                    return Mark ? <Mark className="w-4 h-4" /> : <CBLogo size="sm" />;
                  })()}
                </div>
                <div className="flex-1 pt-1.5">
                  <div className="flex gap-1 items-center">
                    <span className="w-2 h-2 rounded-full bg-zinc-400 dark:bg-zinc-600 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 rounded-full bg-zinc-400 dark:bg-zinc-600 animate-bounce" style={{ animationDelay: '120ms' }} />
                    <span className="w-2 h-2 rounded-full bg-zinc-400 dark:bg-zinc-600 animate-bounce" style={{ animationDelay: '240ms' }} />
                    {anyPendingInActive && (
                      <span className="ml-3 text-[11px] text-zinc-500 dark:text-zinc-400 italic" data-testid="ai-thinking-background-hint">
                        Studio AI is thinking — you can keep working, I'll save the answer here.
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        <Composer
          pendingAttachment={pendingAttachment}
          setPendingAttachment={setPendingAttachment}
          modelLacksVision={modelLacksVision}
          activeModel={activeModel}
          chosenModel={chosenModel}
          setChosenModel={setChosenModel}
          suggestedVisionModel={suggestedVisionModel}
          dragActive={dragActive}
          setDragActive={setDragActive}
          uploadFile={uploadFile}
          input={input}
          setInput={setInput}
          sending={sending}
          handleKeyDown={handleKeyDown}
          renderModelPicker={renderModelPicker}
          fileInputRef={fileInputRef}
          handleAttach={handleAttach}
          activeId={activeId}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          currentWorkspace={currentWorkspace}
          brainMode={brainMode}
          setBrainMode={setBrainMode}
          imageMode={imageMode}
          setImageMode={setImageMode}
          setMemoryOpen={setMemoryOpen}
          sendMessage={sendMessage}
          showFreeFallbackHint={showFreeFallbackHint}
          usage={usage}
          freeFallbackModel={freeFallbackModel}
        />
      </main>

      {/* Cross-thread workspace memory — accessible from the composer's
          Memory button. Renders as a right-side sheet so it doesn't fight
          the chat layout for space. */}
      <MemoryPanel
        workspaceId={currentWorkspace?.workspace_id}
        open={memoryOpen}
        onOpenChange={setMemoryOpen}
      />
    </div>
  );
}
